<template>
	<div class="full-image" v-if="show" :before-close="close" :modal="true">
		<div class="mask" @click="close"></div>
		<div class="image-box">
			<video v-if="isVideo" :src="url" controls autoplay playsinline></video>
			<img v-else ref="img" :src="url" :style="imgStyle" :class="{ 'grabbing': dragging }"
				@mousedown="onMousedown" @wheel.prevent="onWheel" @dblclick="resetTransform" draggable="false" />
		</div>

		<!-- 画廊导航：左右点击区域 -->
		<template v-if="hasGallery && !isVideo">
			<div class="nav-zone nav-left" @click.stop="prevImage" @mousedown.stop @wheel.stop>
				<i class="el-icon-arrow-left"></i>
			</div>
			<div class="nav-zone nav-right" @click.stop="nextImage" @mousedown.stop @wheel.stop>
				<i class="el-icon-arrow-right"></i>
			</div>
			<div class="img-counter">{{ currentIndex + 1 }} / {{ urls.length }}</div>
		</template>

		<!-- 图片工具栏(缩放/旋转/复位/OCR) -->
		<div class="toolbar" v-if="!isVideo" @click.stop @mousedown.stop @wheel.stop>
			<span class="tool-btn" title="放大" @click="zoomBy(0.2)"><i class="el-icon-zoom-in"></i></span>
			<span class="tool-btn" title="缩小" @click="zoomBy(-0.2)"><i class="el-icon-zoom-out"></i></span>
			<span class="scale-txt">{{ Math.round(scale * 100) }}%</span>
			<span class="tool-btn" title="向左旋转" @click="rotateBy(-90)"><i class="el-icon-refresh-left"></i></span>
			<span class="tool-btn" title="向右旋转" @click="rotateBy(90)"><i class="el-icon-refresh-right"></i></span>
			<span class="tool-btn" title="复位" @click="resetTransform"><i class="el-icon-refresh"></i></span>
			<span class="tool-btn ocr-btn" title="识别图片文字(尽力而为)" @click="runOcr"
				:class="{ disabled: ocr.running }">
				<i class="el-icon-document-copy"></i>
				<span class="ocr-label">{{ ocr.running ? '识别中' + (ocr.progress ? ' ' + ocr.progress + '%' : '') : '文字识别' }}</span>
			</span>
		</div>

		<!-- OCR 结果面板 -->
		<div class="ocr-panel" v-if="!isVideo && ocr.visible" @click.stop @mousedown.stop @wheel.stop>
			<div class="ocr-head">
				<span>文字识别结果<span class="ocr-tip">（尽力而为，可能存在误差）</span></span>
				<span class="ocr-actions">
					<span class="ocr-op" @click="copyOcr" v-if="ocr.text">复制</span>
					<span class="ocr-op" @click="ocr.visible = false">关闭</span>
				</span>
			</div>
			<div class="ocr-body">
				<div v-if="ocr.running" class="ocr-loading">正在识别，请稍候…{{ ocr.progress ? ' ' + ocr.progress + '%' : '' }}</div>
				<div v-else-if="ocr.error" class="ocr-error">{{ ocr.error }}</div>
				<pre v-else-if="ocr.text">{{ ocr.text }}</pre>
				<div v-else class="ocr-empty">未识别到文字</div>
			</div>
		</div>

		<div class="close" @click="close"><i class="el-icon-close"></i></div>
	</div>
</template>

<script>
export default {
	name: "fullImage",
	data() {
		return {
			show: false,
			url: '',
			isVideo: false,
			// 画廊浏览
			urls: [],
			currentIndex: 0,
			// 变换状态
			scale: 1,
			rotate: 0,
			offsetX: 0,
			offsetY: 0,
			dragging: false,
			dragStart: { x: 0, y: 0, ox: 0, oy: 0 },
			// OCR 状态
			ocr: { visible: false, running: false, progress: 0, text: '', error: '' }
		}
	},
	computed: {
		imgStyle() {
			return {
				transform: `translate(-50%, -50%) translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale}) rotate(${this.rotate}deg)`
			};
		},
		hasGallery() {
			return this.urls && this.urls.length > 1;
		}
	},
	methods: {
		open(url, urls) {
			this.show = true;
			this.url = url;
			this.isVideo = false;
			// 画廊模式：接收图片URL列表，计算当前索引
			if (urls && urls.length > 1) {
				this.urls = urls;
				this.currentIndex = urls.indexOf(url);
				if (this.currentIndex < 0) this.currentIndex = 0;
			} else {
				this.urls = [];
				this.currentIndex = 0;
			}
			this.resetTransform();
			this.resetOcr();
			window.addEventListener('keydown', this.onKeydown);
		},
		openVideo(url) {
			this.show = true;
			this.url = url;
			this.isVideo = true;
			window.addEventListener('keydown', this.onKeydown);
		},
		close() {
			this.onDragEnd();
			this.show = false;
			this.url = '';
			this.isVideo = false;
			this.urls = [];
			this.currentIndex = 0;
			this.resetTransform();
			this.resetOcr();
			window.removeEventListener('keydown', this.onKeydown);
		},
		onKeydown(e) {
			if (e.key === 'Escape' || e.keyCode === 27) {
				this.close();
			} else if (e.key === 'ArrowLeft' || e.keyCode === 37) {
				this.prevImage();
			} else if (e.key === 'ArrowRight' || e.keyCode === 39) {
				this.nextImage();
			}
		},
		prevImage() {
			if (!this.hasGallery) return;
			if (this.currentIndex <= 0) return; // 到第一张停止，不循环
			this.currentIndex--;
			this.url = this.urls[this.currentIndex];
			this.resetTransform();
			this.resetOcr();
		},
		nextImage() {
			if (!this.hasGallery) return;
			if (this.currentIndex >= this.urls.length - 1) return; // 到最后一张停止，不循环
			this.currentIndex++;
			this.url = this.urls[this.currentIndex];
			this.resetTransform();
			this.resetOcr();
		},
		resetTransform() {
			this.scale = 1;
			this.rotate = 0;
			this.offsetX = 0;
			this.offsetY = 0;
		},
		resetOcr() {
			this.ocr = { visible: false, running: false, progress: 0, text: '', error: '' };
		},
		zoomBy(delta) {
			let next = this.scale + delta;
			if (next < 0.2) next = 0.2;
			if (next > 8) next = 8;
			this.scale = Math.round(next * 100) / 100;
		},
		rotateBy(deg) {
			this.rotate = (this.rotate + deg) % 360;
		},
		onWheel(e) {
			this.zoomBy(e.deltaY < 0 ? 0.2 : -0.2);
		},
		onMousedown(e) {
			// 只响应左键拖拽, 右键保留给浏览器原生菜单(复制图片等)
			if (e.button !== 0) {
				return;
			}
			e.preventDefault();
			this.dragging = true;
			this.dragStart = { x: e.clientX, y: e.clientY, ox: this.offsetX, oy: this.offsetY };
			window.addEventListener('mousemove', this.onDragMove);
			window.addEventListener('mouseup', this.onDragEnd);
		},
		onDragMove(e) {
			if (!this.dragging) return;
			this.offsetX = this.dragStart.ox + (e.clientX - this.dragStart.x);
			this.offsetY = this.dragStart.oy + (e.clientY - this.dragStart.y);
		},
		onDragEnd() {
			this.dragging = false;
			window.removeEventListener('mousemove', this.onDragMove);
			window.removeEventListener('mouseup', this.onDragEnd);
		},
		// 文字识别(尽力而为)：懒加载 tesseract.js，失败不影响其他功能
		async runOcr() {
			if (this.ocr.running || !this.url) return;
			this.ocr.visible = true;
			this.ocr.running = true;
			this.ocr.progress = 0;
			this.ocr.text = '';
			this.ocr.error = '';
			try {
				const mod = await import('tesseract.js');
				const Tesseract = mod.default || mod;
				const { data } = await Tesseract.recognize(this.url, 'chi_sim+eng', {
					logger: m => {
						if (m && m.status === 'recognizing text' && typeof m.progress === 'number') {
							this.ocr.progress = Math.round(m.progress * 100);
						}
					}
				});
				this.ocr.text = (data && data.text ? data.text : '').trim();
			} catch (e) {
				this.ocr.error = '文字识别失败（可能是网络受限或图片过大），请重试或手动查看。';
			} finally {
				this.ocr.running = false;
			}
		},
		copyOcr() {
			if (!this.ocr.text) return;
			const done = () => this.$message && this.$message.success('已复制');
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(this.ocr.text).then(done).catch(() => { });
			} else {
				const ta = document.createElement('textarea');
				ta.value = this.ocr.text;
				document.body.appendChild(ta);
				ta.select();
				try { document.execCommand('copy'); done(); } catch (e) { /* fallback ignore */ }
				document.body.removeChild(ta);
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.full-image {
	position: fixed;
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
	bottom: 0;
	right: 0;
	// 全屏媒体查看器必须覆盖置顶栏、定位提示、侧边栏等所有页面元素
	z-index: 3000;

	.mask {
		position: fixed;
		width: 100%;
		height: 100%;
		left: 0;
		top: 0;
		background: black;
		opacity: 0.5;

	}

	.image-box {
		position: relative;
		width: 100%;
		height: 100%;

		img {
			position: absolute;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			max-height: 100%;
			max-width: 100%;
			cursor: grab;
			user-select: none;
			transition: transform 0.05s linear;

			&.grabbing {
				cursor: grabbing;
			}
		}

		video {
			position: absolute;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			max-height: 90%;
			max-width: 90%;
			outline: none;
		}
	}

	.toolbar {
		position: fixed;
		left: 50%;
		bottom: 30px;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 14px;
		background: rgba(40, 40, 40, 0.85);
		border-radius: 26px;
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);

		.tool-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			min-width: 34px;
			height: 34px;
			padding: 0 8px;
			border-radius: 17px;
			cursor: pointer;
			color: #fff;
			transition: background 0.2s;

			&:hover {
				background: rgba(255, 255, 255, 0.15);
			}

			i {
				font-size: 18px;
			}

			&.disabled {
				opacity: 0.6;
				cursor: not-allowed;
			}
		}

		.ocr-btn {
			background: rgba(91, 141, 239, 0.35);

			.ocr-label {
				font-size: 13px;
				margin-left: 4px;
			}
		}

		.scale-txt {
			color: rgba(255, 255, 255, 0.85);
			font-size: 13px;
			min-width: 44px;
			text-align: center;
		}
	}

	.ocr-panel {
		position: fixed;
		right: 24px;
		top: 70px;
		width: 320px;
		max-height: 60vh;
		display: flex;
		flex-direction: column;
		background: #262a3d;
		border-radius: 12px;
		box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
		overflow: hidden;

		.ocr-head {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 12px 14px;
			color: #fff;
			font-size: 14px;
			border-bottom: 1px solid rgba(255, 255, 255, 0.08);

			.ocr-tip {
				font-size: 11px;
				color: rgba(255, 255, 255, 0.4);
			}

			.ocr-actions .ocr-op {
				margin-left: 12px;
				font-size: 13px;
				color: #5b8def;
				cursor: pointer;
			}
		}

		.ocr-body {
			padding: 12px 14px;
			overflow: auto;
			color: #e6e6e6;
			font-size: 13px;

			pre {
				white-space: pre-wrap;
				word-break: break-all;
				margin: 0;
				font-family: inherit;
				line-height: 1.6;
			}

			.ocr-loading {
				color: rgba(255, 255, 255, 0.6);
			}

			.ocr-error {
				color: #ff8b8b;
				line-height: 1.6;
			}

			.ocr-empty {
				color: rgba(255, 255, 255, 0.4);
			}
		}
	}

	.close {
		display: flex;
		align-items: center;
		justify-content: center;
		position: fixed;
		top: 20px;
		right: 20px;
		cursor: pointer;
		background: #333;
		border-radius: 50%;
		padding: 10px;
		opacity: 0.5;
		z-index: 10;

		i {
			font-weight: bold;
			font-size: 20px;
			color: white;
		}
	}

	// 画廊导航区域
	.nav-zone {
		position: fixed;
		top: 0;
		bottom: 0;
		width: 120px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		z-index: 1;

		i {
			font-size: 48px;
			color: rgba(255, 255, 255, 0.5);
			transition: color 0.2s, transform 0.2s;
		}

		&:hover i {
			color: rgba(255, 255, 255, 0.9);
			transform: scale(1.15);
		}
	}

	.nav-left {
		left: 0;
	}

	.nav-right {
		right: 0;
	}

	.img-counter {
		position: fixed;
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		padding: 6px 16px;
		background: rgba(40, 40, 40, 0.75);
		border-radius: 16px;
		color: #fff;
		font-size: 14px;
		z-index: 1;
		pointer-events: none;
	}
}
</style>
